import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import Decimal from 'decimal.js';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css, useTheme } from 'styled-components';

import { Settlement } from 'apis/restaurant-api/merchant-admin/settlements/types';
import { InfiniteList } from 'common/components';

import LocalizedMonetaryAmount from './LocalizedMonetaryAmount';
import {
  ROW_HEIGHT,
  SETTLEMENT_REASONS,
  SETTLEMENT_STATUSES,
  SETTLEMENT_STATUS_TAG_VARIANT,
  SETTLEMENT_TYPES,
  SETTLEMENT_REASON_TAG_VARIANT,
} from './constants';

type Props = {
  className?: string;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  onClickSettlement: (settlementId: string) => void;
  settlements: Settlement[];
  selectedSettlementId?: Settlement['id'];
  timezone: string;
};

// These values are percentages and should always add up to 100
export const COLUMN_WIDTHS = {
  orderNumber: 10,
  customer: 15,
  amount: 10,
  settlementType: 15,
  reason: 17,
  status: 16,
  createdAt: 17,
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: hidden; /* Avoids scroll bar flickering when zooming in */
`;

const ListHeader = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 100%;
`;

const ColumnHeader = styled.div`
  ${typographyCss.Overline3()}
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  color: ${cbSemanticCssVars.textSecondary};
  display: flex;
  height: ${ROW_HEIGHT}px;
  padding: 0 24px;
  text-align: start;
`;

const OrderNumberHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.orderNumber}%;
  padding-inline-start: 29px;
`;

const CustomerHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.customer}%;
`;

const AmountHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.amount}%;
  justify-content: flex-end;
`;

const SettlementTypeHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.settlementType}%;
`;

const ReasonHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.reason}%;
`;

const StatusHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.status}%;
`;

const CreatedAtHeader = styled(ColumnHeader)`
  width: ${COLUMN_WIDTHS.createdAt}%;
`;

const Wrapper = styled.div`
  flex: 1 1 auto;
`;

const Cell = styled.div`
  ${typographyCss.Body3()}
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  display: flex;
  padding: 0 24px;
  text-align: start;
`;

const Row = styled.div<{ selected?: boolean }>`
  cursor: pointer;
  display: flex;
  width: 100%;
  ${({ selected }) =>
    selected
      ? css`
          background-color: ${colors.wolt8};
        `
      : css`
          &:hover {
            /* ignore-color */
            background-color: ${colors.pepper4};
          }
        `};
`;

const OrderNumberCell = styled(Cell)`
  width: ${COLUMN_WIDTHS.orderNumber}%;
  padding-inline-start: 29px;
`;

const CustomerCell = styled(Cell)`
  width: ${COLUMN_WIDTHS.customer}%;
`;

const AmountCell = styled(Cell)<{ positive: boolean }>`
  width: ${COLUMN_WIDTHS.amount}%;
  justify-content: flex-end;
  color: ${({ positive }) => (positive ? colors.lime100 : colors.strawberry100)};
`;

const SettlementTypeCell = styled(Cell)`
  width: ${COLUMN_WIDTHS.settlementType}%;
`;

const ReasonCell = styled(Cell)`
  width: ${COLUMN_WIDTHS.reason}%;
`;

const StatusCell = styled(Cell)`
  width: ${COLUMN_WIDTHS.status}%;
`;

const CreatedAtCell = styled(Cell)`
  width: ${COLUMN_WIDTHS.createdAt}%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  & > *:first-child {
    width: 100%;
  }

  & > *:last-child {
    color: ${cssThemedValue({ light: colors.pepper48, dark: colors.salt56 })};
    ${typographyCss.Small()}
    font-weight: 400;
    min-height: 16px;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const EmptyMessage = styled(Empty)`
  margin: 48px 16px;
`;

const SettlementList = ({
  className,
  fetchingNextPage,
  fetchingPreviousPage,
  onFetchNextPage,
  onFetchPreviousPage,
  onClickSettlement,
  settlements,
  selectedSettlementId,
  timezone,
}: Props) => {
  const intl = useIntl();
  const theme = useTheme();

  const isEmpty = settlements.length === 0 && !onFetchNextPage && !onFetchPreviousPage;
  return (
    <Root className={className}>
      <ListHeader>
        <OrderNumberHeader>
          <FormattedMessage id="settlements.columns.order-number" />
        </OrderNumberHeader>
        <CustomerHeader>
          <FormattedMessage id="settlements.columns.customer" />
        </CustomerHeader>
        <AmountHeader>
          <FormattedMessage id="settlements.columns.amount" />
        </AmountHeader>
        <SettlementTypeHeader>
          <FormattedMessage id="settlements.columns.settlement-type" />
        </SettlementTypeHeader>
        <ReasonHeader>
          <FormattedMessage id="settlements.columns.reason" />
        </ReasonHeader>
        <StatusHeader>
          <FormattedMessage id="settlements.columns.status" />
        </StatusHeader>
        <CreatedAtHeader>
          <FormattedMessage id="settlements.columns.created-at" />
        </CreatedAtHeader>
      </ListHeader>
      <Wrapper>
        {isEmpty ? (
          <EmptyMessage title={intl.formatMessage({ id: 'settlements.messages.no-settlements' })} />
        ) : (
          <InfiniteList
            fetchingNextPage={fetchingNextPage}
            fetchingPreviousPage={fetchingPreviousPage}
            onFetchNextPage={onFetchNextPage}
            onFetchPreviousPage={onFetchPreviousPage}
            itemSize={ROW_HEIGHT}
            items={settlements}
          >
            {({ item: settlement, style }: { item: Settlement; style: React.CSSProperties }) => {
              const status = settlement.settled ? 'settled' : 'not_settled';
              const amountDetails = {
                amount: new Decimal(settlement.amount.amount),
                currency: settlement.amount.currency,
                positive: new Decimal(settlement.amount.amount).greaterThanOrEqualTo(0),
              };
              const selected = settlement.id === selectedSettlementId;
              return (
                <Row
                  dir={theme.cbDirection}
                  style={style as any}
                  selected={selected}
                  onClick={() => {
                    onClickSettlement(settlement.id);
                  }}
                >
                  <OrderNumberCell>{settlement.order_number ? `#${settlement.order_number}` : '-'}</OrderNumberCell>
                  <CustomerCell>
                    {settlement.customer_name
                      ? `${settlement.customer_name.first_name} ${settlement.customer_name.last_name}`
                      : '-'}
                  </CustomerCell>
                  <AmountCell positive={amountDetails.positive}>
                    <LocalizedMonetaryAmount
                      amount={amountDetails.amount.toNumber()}
                      currency={amountDetails.currency}
                      numberFormatOptions={{ signDisplay: 'exceptZero' }}
                    />
                  </AmountCell>
                  <SettlementTypeCell>
                    {SETTLEMENT_TYPES[settlement.type] && <FormattedMessage id={SETTLEMENT_TYPES[settlement.type]} />}
                  </SettlementTypeCell>
                  <ReasonCell>
                    {settlement.reason && SETTLEMENT_REASONS[settlement.reason] ? (
                      <Tag variant={SETTLEMENT_REASON_TAG_VARIANT} uppercase>
                        <FormattedMessage id={SETTLEMENT_REASONS[settlement.reason]} />
                      </Tag>
                    ) : (
                      <>-</>
                    )}
                  </ReasonCell>
                  <StatusCell>
                    {SETTLEMENT_STATUSES[status] ? (
                      <Tag variant={SETTLEMENT_STATUS_TAG_VARIANT[status]} uppercase>
                        <FormattedMessage id={SETTLEMENT_STATUSES[status]} />
                      </Tag>
                    ) : (
                      <>-</>
                    )}
                  </StatusCell>
                  <CreatedAtCell>
                    <div>
                      {new Intl.DateTimeFormat(undefined, {
                        dateStyle: 'short',
                        timeStyle: 'short',
                        timeZone: timezone,
                      }).format(new Date(settlement.created_at))}
                    </div>
                    <div>{DateTime.fromISO(settlement.created_at).setLocale(intl.locale).toRelative()}</div>
                  </CreatedAtCell>
                </Row>
              );
            }}
          </InfiniteList>
        )}
      </Wrapper>
    </Root>
  );
};

export default SettlementList;
